import React, { useContext, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import PeopleIcon from '@mui/icons-material/People';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Parser from 'html-react-parser';
import { UserContextType, useAuth } from '../auth';
import { GroupApiError } from '../service/GroupService';
import { groupInvitationService } from '../service/GroupInvitationService';
import { AcceptGroupInvitationRequest, GroupInvitationSetup } from '../types/group';
import RichText from '../common/RichTextControl';
import AiRewriteButton from '../common/AiRewriteButton';
import { useProofreadCheck } from '../common/useProofreadCheck';
import { editorial } from '../common/editorialTheme';
import GroupEventFields, { GroupEventFormValue, emptyGroupEventForm } from '../admin/groups/GroupEventFields';
import { groupCardSx } from './Groups';
import { formatEventRange, isBlankHtml, validateEventTimes } from './groupFormat';

const Emitter = require('tiny-emitter/instance');

const MAX_EVENTS = 10;

const steps = ['Name & meeting info', 'Description', 'Welcome update', 'Events', 'Review & create'];

// Everything the leader has entered so far. Kept in sessionStorage (keyed by the invitation
// token) so a refresh or a detour through sign-in doesn't lose it. Nothing reaches the server
// until Create Group.
interface SetupDraft {
    step: number;
    name: string;
    meetingInfo: string;
    description: string;
    descriptionPrefilled: boolean;
    updateTitle: string;
    updateBody: string;
    skipUpdate: boolean;
    events: GroupEventFormValue[];
    eventForm: GroupEventFormValue;
    editingEventIndex: number | null;
    isActive: boolean;
}

const emptyDraft: SetupDraft = {
    step: 0,
    name: '',
    meetingInfo: '',
    description: '',
    descriptionPrefilled: false,
    updateTitle: '',
    updateBody: '',
    skipUpdate: false,
    events: [],
    eventForm: emptyGroupEventForm,
    editingEventIndex: null,
    isActive: false,
};

const storageKey = (token: string) => `groupSetup:${token}`;

const loadDraft = (token?: string): SetupDraft => {
    if (!token) return emptyDraft;
    try {
        const saved = sessionStorage.getItem(storageKey(token));
        if (saved) return { ...emptyDraft, ...JSON.parse(saved) };
    } catch (err) {
        // Storage unavailable or corrupt - start fresh.
    }
    return emptyDraft;
};

const saveDraft = (token: string, draft: SetupDraft) => {
    try {
        sessionStorage.setItem(storageKey(token), JSON.stringify(draft));
    } catch (err) {
        // Storage unavailable (private mode, quota) - progress just won't survive a refresh.
    }
};

const clearDraft = (token: string) => {
    try {
        sessionStorage.removeItem(storageKey(token));
    } catch (err) {
        // Nothing to clean up.
    }
};

const escapeHtml = (text: string) =>
    text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

// The admin's plain-text description as rich-text paragraphs: blank lines start a new
// paragraph, single line breaks become <br>.
const plainTextToHtml = (text: string): string =>
    text
        .replace(/\r\n/g, '\n')
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter((p) => p.length > 0)
        .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
        .join('');

const hasUpdate = (draft: SetupDraft) =>
    !draft.skipUpdate && (draft.updateTitle.trim().length > 0 || !isBlankHtml(draft.updateBody));

const isFormStarted = (form: GroupEventFormValue) =>
    !!(form.title.trim() || form.startDateTime || form.endDateTime || form.location.trim() || form.description.trim());

const toEventRequest = (event: GroupEventFormValue) => ({
    title: event.title.trim(),
    description: event.description.trim() || null,
    startDateTime: event.startDateTime,
    endDateTime: event.endDateTime,
    location: event.location.trim() || null,
});

const reviewSectionSx = { py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 };

type LoadError = 'notFound' | 'forbidden' | 'other';

const MessageCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ ...groupCardSx, mt: 2 }}>
        <Typography
            component="h1"
            sx={{ fontFamily: editorial.headlineFont, fontWeight: 600, color: editorial.primary, fontSize: '1.75rem', mb: 1 }}
        >
            {title}
        </Typography>
        {children}
    </Box>
);

const GroupSetup: React.FC = () => {
    const { token: invitationToken } = useParams<string>();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const { IsAuthenticated, getToken, logout, setAuth, CanManageGroups } = useContext<UserContextType>(useAuth);
    const signedIn = !!IsAuthenticated && IsAuthenticated();
    const authToken = getToken ? getToken() : '';
    // AI Rewrite / spell check need the GroupAdmin role. A sign-in from before the invitation
    // doesn't carry it, but loading the invitation swaps in a fresh token (see loadSetup), so
    // this is only false for a moment; the spell check fails open on its own.
    const canUseAi = !!CanManageGroups && CanManageGroups();

    const [setup, setSetup] = useState<GroupInvitationSetup | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<LoadError | null>(null);
    const [draft, setDraft] = useState<SetupDraft>(() => loadDraft(invitationToken));
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');

    const descriptionProofread = useProofreadCheck('group', 'group description');
    const updateProofread = useProofreadCheck('group-update', 'update');

    const update = (changes: Partial<SetupDraft>) => setDraft((prev) => ({ ...prev, ...changes }));

    useEffect(() => {
        if (invitationToken) saveDraft(invitationToken, draft);
    }, [invitationToken, draft]);

    const loadSetup = async () => {
        if (!invitationToken || !authToken) return;
        setLoading(true);
        setLoadError(null);
        try {
            const result = await groupInvitationService.getSetup(invitationToken, authToken);
            // Signed in before the invitation granted GroupAdmin: swap in the fresh token so the
            // wizard's AI Rewrite (role-gated) works. The token change re-runs this load once.
            if (result.auth && setAuth) setAuth(result.auth);
            setSetup(result);
            // Start the description from the admin's note, once.
            setDraft((prev) =>
                prev.descriptionPrefilled
                    ? prev
                    : { ...prev, description: plainTextToHtml(result.description || ''), descriptionPrefilled: true }
            );
        } catch (err) {
            if (err instanceof GroupApiError && err.status === 404) setLoadError('notFound');
            else if (err instanceof GroupApiError && err.status === 403) setLoadError('forbidden');
            else setLoadError('other');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSetup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invitationToken, authToken]);

    if (!signedIn) {
        return <Navigate to="/login" replace state={{ returnTo: location.pathname }} />;
    }

    const goTo = (step: number) => {
        update({ step });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ---- Step handlers ----
    const nextFromName = () => {
        if (!draft.name.trim()) {
            Emitter.emit('notify', 'Please give your group a name.', 'error');
            return;
        }
        goTo(1);
    };

    const nextFromDescription = async () => {
        if (isBlankHtml(draft.description)) {
            Emitter.emit('notify', 'Please describe your group.', 'error');
            return;
        }
        await descriptionProofread.checkThenSave(draft.description, (description) => {
            setDraft((prev) => ({ ...prev, description, step: 2 }));
        });
    };

    const nextFromUpdate = async () => {
        const titleBlank = !draft.updateTitle.trim();
        const bodyBlank = isBlankHtml(draft.updateBody);
        if (titleBlank && bodyBlank) {
            update({ skipUpdate: true, step: 3 });
            return;
        }
        if (titleBlank || bodyBlank) {
            Emitter.emit('notify', 'Please add both a title and a message, or Skip this step.', 'error');
            return;
        }
        await updateProofread.checkThenSave(draft.updateBody, (updateBody) => {
            setDraft((prev) => ({ ...prev, updateBody, skipUpdate: false, step: 3 }));
        });
    };

    const saveEventForm = () => {
        const error = validateEventTimes(draft.eventForm);
        if (error) {
            Emitter.emit('notify', error, 'error');
            return;
        }
        const { editingEventIndex, eventForm, events } = draft;
        if (editingEventIndex === null && events.length >= MAX_EVENTS) {
            Emitter.emit('notify', `You can add up to ${MAX_EVENTS} events here. Add more later from Manage Groups.`, 'error');
            return;
        }
        const nextEvents = editingEventIndex === null
            ? [...events, eventForm]
            : events.map((e, i) => (i === editingEventIndex ? eventForm : e));
        nextEvents.sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime());
        update({ events: nextEvents, eventForm: emptyGroupEventForm, editingEventIndex: null });
    };

    const nextFromEvents = () => {
        if (isFormStarted(draft.eventForm)) {
            Emitter.emit(
                'notify',
                draft.editingEventIndex === null
                    ? 'Click "Add Event" to keep the event you started, or clear the form.'
                    : 'Click "Save Event" to keep your changes, or Cancel.',
                'warning'
            );
            return;
        }
        goTo(4);
    };

    const handleCreate = async () => {
        if (!invitationToken || !authToken) return;
        setCreateError('');
        const request: AcceptGroupInvitationRequest = {
            name: draft.name.trim(),
            meetingInfo: draft.meetingInfo.trim() || null,
            description: draft.description,
            isActive: draft.isActive,
            welcomeUpdate: hasUpdate(draft) ? { title: draft.updateTitle.trim(), body: draft.updateBody } : null,
            events: draft.events.map(toEventRequest),
        };
        setCreating(true);
        try {
            const response = await groupInvitationService.accept(invitationToken, request, authToken);
            clearDraft(invitationToken);
            // The fresh token carries the GroupAdmin role, so the admin pages let them in.
            if (setAuth) setAuth(response.auth);
            Emitter.emit(
                'notify',
                draft.isActive ? 'Group created and live' : "Group created — it's hidden until you turn it on",
                'success'
            );
            navigate(`/admin/groups/${response.group.id}`);
        } catch (err) {
            if (err instanceof GroupApiError && err.status === 400) {
                setCreateError(err.message);
            } else if (err instanceof GroupApiError && err.status === 409) {
                // Already accepted (e.g. in another tab) - show the "already set up" card.
                clearDraft(invitationToken);
                await loadSetup();
            } else if (err instanceof GroupApiError && err.status === 404) {
                setLoadError('notFound');
            } else if (err instanceof GroupApiError && err.status === 403) {
                setLoadError('forbidden');
            } else {
                setCreateError('Something went wrong creating your group. Please try again.');
            }
        } finally {
            setCreating(false);
        }
    };

    const handleSignOut = () => {
        if (logout) logout();
        navigate('/login', { replace: true, state: { returnTo: location.pathname } });
    };

    // ---- Step content ----
    const renderNameStep = () => (
        <Stack spacing={2}>
            <Typography color="text.secondary">
                What should people call your group, and when and where do you meet?
            </Typography>
            <TextField
                label="Group name"
                value={draft.name}
                onChange={(e) => update({ name: e.target.value })}
                required
                fullWidth
                autoFocus
            />
            <TextField
                label="Meeting info"
                placeholder="e.g. Tuesdays at 7pm in the fellowship hall"
                value={draft.meetingInfo}
                onChange={(e) => update({ meetingInfo: e.target.value })}
                fullWidth
            />
        </Stack>
    );

    const renderDescriptionStep = () => (
        <Stack spacing={2}>
            <Typography color="text.secondary">
                This is what people see on the Groups page. We've started it for you from the invitation
                {canUseAi ? ' — edit it, or use AI Rewrite to polish it.' : ' — edit it however you like.'}
            </Typography>
            {canUseAi && (
                <Stack direction="row" justifyContent="flex-end">
                    <AiRewriteButton
                        content={draft.description}
                        contentType="group"
                        isHtml
                        disabled={descriptionProofread.isChecking}
                        onAccept={(description) => update({ description })}
                    />
                </Stack>
            )}
            <Box sx={{ border: '1px solid #ccc', borderRadius: 1, minHeight: '200px' }}>
                <RichText text={draft.description} onChange={(html: string) => update({ description: html })} />
            </Box>
        </Stack>
    );

    const renderUpdateStep = () => (
        <Stack spacing={2}>
            <Typography color="text.secondary">
                Optional: post a first update to welcome members. Only approved members see updates. You can Skip
                this and post one later.
            </Typography>
            <TextField
                label="Title"
                placeholder="e.g. Welcome!"
                value={draft.updateTitle}
                onChange={(e) => update({ updateTitle: e.target.value })}
                fullWidth
            />
            <Box sx={{ border: '1px solid #ccc', borderRadius: 1, minHeight: '200px' }}>
                <RichText text={draft.updateBody} onChange={(html: string) => update({ updateBody: html })} />
            </Box>
            {canUseAi && (
                <Box>
                    <AiRewriteButton
                        content={draft.updateBody}
                        contentType="group-update"
                        isHtml
                        disabled={updateProofread.isChecking}
                        onAccept={(updateBody) => update({ updateBody })}
                    />
                </Box>
            )}
        </Stack>
    );

    const renderEventList = (withActions: boolean) =>
        draft.events.map((event, i) => (
            <React.Fragment key={`${event.startDateTime}-${i}`}>
                {i > 0 && <Divider />}
                <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600 }}>{event.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{formatEventRange(event)}</Typography>
                        {event.location && (
                            <Typography variant="body2" color="text.secondary">{event.location}</Typography>
                        )}
                        {event.description && (
                            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>{event.description}</Typography>
                        )}
                    </Box>
                    {withActions && (
                        <Stack direction="row">
                            <Tooltip title="Edit">
                                <IconButton onClick={() => update({ eventForm: event, editingEventIndex: i })}>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                                <IconButton
                                    onClick={() =>
                                        setDraft((prev) => ({
                                            ...prev,
                                            events: prev.events.filter((_, j) => j !== i),
                                            ...(prev.editingEventIndex === i
                                                ? { eventForm: emptyGroupEventForm, editingEventIndex: null }
                                                : prev.editingEventIndex !== null && prev.editingEventIndex > i
                                                    ? { editingEventIndex: prev.editingEventIndex - 1 }
                                                    : {}),
                                        }))
                                    }
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}
                </Box>
            </React.Fragment>
        ));

    const renderEventsStep = () => {
        const editing = draft.editingEventIndex !== null;
        const atLimit = !editing && draft.events.length >= MAX_EVENTS;
        return (
            <Stack spacing={2}>
                <Typography color="text.secondary">
                    Optional: add your upcoming meetings or get-togethers (up to {MAX_EVENTS}). You can Skip this and add
                    events later.
                </Typography>
                <Alert severity="info">
                    Once your group is active, its next upcoming event shows automatically as an announcement on the website.
                </Alert>

                {draft.events.length > 0 && <Paper variant="outlined" sx={{ px: 2 }}>{renderEventList(true)}</Paper>}

                {atLimit ? (
                    <Typography color="text.secondary">
                        That's the most you can add here. You can add more from Manage Groups after your group is created.
                    </Typography>
                ) : (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {editing ? 'Edit event' : draft.events.length > 0 ? 'Add another event' : 'Add an event'}
                        </Typography>
                        <GroupEventFields
                            value={draft.eventForm}
                            onChange={(eventForm) => update({ eventForm })}
                            hideAiRewrite={!canUseAi}
                        />
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                            {(editing || isFormStarted(draft.eventForm)) && (
                                <Button onClick={() => update({ eventForm: emptyGroupEventForm, editingEventIndex: null })}>
                                    {editing ? 'Cancel' : 'Clear'}
                                </Button>
                            )}
                            <Button variant="outlined" startIcon={editing ? <EditIcon /> : <AddIcon />} onClick={saveEventForm}>
                                {editing ? 'Save Event' : 'Add Event'}
                            </Button>
                        </Stack>
                    </Paper>
                )}
            </Stack>
        );
    };

    const editButton = (step: number) => (
        <Button size="small" startIcon={<EditIcon />} onClick={() => goTo(step)} disabled={creating}>
            Edit
        </Button>
    );

    const renderReviewStep = () => (
        <Stack spacing={2}>
            <Typography color="text.secondary">
                Here's how your group will look. Check everything, then click Create Group.
            </Typography>

            {/* Same look as the public Groups page card. */}
            <Box sx={{ ...groupCardSx, p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography
                        component="h2"
                        sx={{ fontFamily: editorial.headlineFont, fontWeight: 600, color: editorial.primary, fontSize: '1.5rem', mb: 1 }}
                    >
                        {draft.name}
                    </Typography>
                    {editButton(0)}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, color: editorial.secondary }}>
                    {draft.meetingInfo.trim() && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <ScheduleIcon fontSize="small" />
                            <Typography sx={{ fontFamily: editorial.bodyFont, fontSize: '0.9rem' }}>{draft.meetingInfo}</Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <PeopleIcon fontSize="small" />
                        <Typography sx={{ fontFamily: editorial.bodyFont, fontSize: '0.9rem' }}>1 member</Typography>
                    </Box>
                </Box>
                <Box
                    sx={{
                        fontFamily: editorial.bodyFont,
                        color: editorial.textVariant,
                        '& p': { lineHeight: 1.6, m: 0, mb: 1 },
                        '& img': { maxWidth: '100%' },
                    }}
                >
                    {Parser(draft.description || '')}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {editButton(1)}
                </Box>
            </Box>

            <Paper variant="outlined" sx={{ px: 2 }}>
                <Box sx={reviewSectionSx}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Welcome update</Typography>
                        {hasUpdate(draft) ? (
                            <>
                                <Typography sx={{ fontWeight: 600, mt: 0.5 }}>{draft.updateTitle}</Typography>
                                <Box sx={{ '& p': { mt: 0, mb: 1 } }}>{Parser(draft.updateBody)}</Box>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Skipped. You can post one later.</Typography>
                        )}
                    </Box>
                    {editButton(2)}
                </Box>
                <Divider />
                <Box sx={reviewSectionSx}>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Events{draft.events.length > 0 ? ` (${draft.events.length})` : ''}
                        </Typography>
                        {draft.events.length > 0 ? (
                            renderEventList(false)
                        ) : (
                            <Typography variant="body2" color="text.secondary">None yet. You can add events later.</Typography>
                        )}
                    </Box>
                    {editButton(3)}
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={draft.isActive}
                            onChange={(e) => update({ isActive: e.target.checked })}
                            disabled={creating}
                        />
                    }
                    label="Show this group on the website now"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {draft.isActive
                        ? 'People can find your group on the Groups page and ask to join right away.'
                        : 'Your group stays hidden until you switch it to Active in Manage Groups.'}
                </Typography>
            </Paper>

            {createError && <Alert severity="error">{createError}</Alert>}
        </Stack>
    );

    const renderStep = (step: number) => {
        switch (step) {
            case 0: return renderNameStep();
            case 1: return renderDescriptionStep();
            case 2: return renderUpdateStep();
            case 3: return renderEventsStep();
            default: return renderReviewStep();
        }
    };

    const busy = creating || descriptionProofread.isChecking || updateProofread.isChecking;

    const renderActions = (step: number) => {
        const next = [nextFromName, nextFromDescription, nextFromUpdate, nextFromEvents][step];
        const checking = (step === 1 && descriptionProofread.isChecking) || (step === 2 && updateProofread.isChecking);
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, mt: 3 }}>
                <Button onClick={() => goTo(step - 1)} disabled={step === 0 || busy}>
                    Back
                </Button>
                <Stack direction="row" spacing={1}>
                    {step === 2 && (
                        <Button onClick={() => update({ skipUpdate: true, step: 3 })} disabled={busy}>Skip</Button>
                    )}
                    {step === 3 && draft.events.length === 0 && (
                        <Button
                            onClick={() => update({ eventForm: emptyGroupEventForm, editingEventIndex: null, step: 4 })}
                            disabled={busy}
                        >
                            Skip
                        </Button>
                    )}
                    {step < 4 ? (
                        <Button variant="contained" onClick={next} disabled={busy}>
                            {checking ? 'Checking...' : 'Next'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleCreate}
                            disabled={busy}
                            startIcon={creating ? <CircularProgress color="inherit" size={16} /> : undefined}
                        >
                            {creating ? 'Creating...' : 'Create Group'}
                        </Button>
                    )}
                </Stack>
            </Box>
        );
    };

    const page = (content: React.ReactNode) => (
        <Box sx={{ backgroundColor: editorial.canvas, minHeight: '100vh', pb: 10 }}>
            <Container maxWidth="md" sx={{ pt: { xs: 4, md: 6 } }}>{content}</Container>
        </Box>
    );

    if (loading && !setup && !loadError) {
        return page(
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError === 'notFound') {
        return page(
            <MessageCard title="This invitation is no longer active">
                <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, mb: 2 }}>
                    It may have been cancelled. If you think this is a mistake, contact the person who invited you.
                </Typography>
                <Button component={RouterLink} to="/groups" variant="outlined">See all groups</Button>
            </MessageCard>
        );
    }

    if (loadError === 'forbidden') {
        return page(
            <MessageCard title="This invitation is for a different account">
                <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, mb: 2 }}>
                    This invitation was sent to a different account. Sign out and sign in with the invited email.
                </Typography>
                <Button variant="contained" onClick={handleSignOut}>Sign out</Button>
            </MessageCard>
        );
    }

    if (loadError === 'other' || !setup) {
        return page(
            <MessageCard title="Something went wrong">
                <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, mb: 2 }}>
                    We couldn't load your invitation. Please try again.
                </Typography>
                <Button variant="outlined" onClick={loadSetup}>Try again</Button>
            </MessageCard>
        );
    }

    if (setup.status === 'Accepted') {
        return page(
            <MessageCard title="Your group is already set up">
                <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, mb: 2 }}>
                    This invitation has already been used to create your group. You can manage it from Manage Groups.
                </Typography>
                <Button
                    component={RouterLink}
                    to={setup.groupId ? `/admin/groups/${setup.groupId}` : '/admin/groups'}
                    variant="contained"
                >
                    Go to your group
                </Button>
            </MessageCard>
        );
    }

    const activeStep = Math.min(Math.max(draft.step, 0), steps.length - 1);

    return page(
        <>
            <Typography
                component="h1"
                sx={{
                    fontFamily: editorial.headlineFont,
                    fontWeight: 600,
                    color: editorial.primary,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    lineHeight: 1.15,
                    mb: 1,
                }}
            >
                Set up your group
            </Typography>
            <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, mb: 3 }}>
                {setup.invitedByName ? `${setup.invitedByName} invited you to lead a small group. ` : ''}
                Nothing is saved until you click Create Group on the last step, and your progress is kept if you refresh.
            </Typography>
            {setup.description && (
                <Box
                    sx={{
                        borderLeft: `3px solid ${editorial.primary}`,
                        pl: 2,
                        py: 1,
                        mb: 3,
                        bgcolor: editorial.sectionAlt,
                        fontFamily: editorial.bodyFont,
                        color: editorial.textVariant,
                        whiteSpace: 'pre-line',
                        fontStyle: 'italic',
                    }}
                >
                    {setup.description}
                </Box>
            )}

            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                {isSmall ? (
                    <Stepper activeStep={activeStep} orientation="vertical">
                        {steps.map((label, i) => (
                            <Step key={label}>
                                <StepLabel optional={i === 2 || i === 3 ? <Typography variant="caption">Optional</Typography> : undefined}>
                                    {label}
                                </StepLabel>
                                <StepContent TransitionProps={{ unmountOnExit: true }}>
                                    {renderStep(i)}
                                    {renderActions(i)}
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                ) : (
                    <>
                        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                            {steps.map((label, i) => (
                                <Step key={label}>
                                    <StepLabel optional={i === 2 || i === 3 ? <Typography variant="caption">Optional</Typography> : undefined}>
                                        {label}
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        {renderStep(activeStep)}
                        {renderActions(activeStep)}
                    </>
                )}
            </Paper>
            {descriptionProofread.proofreadDialog}
            {updateProofread.proofreadDialog}
        </>
    );
};

export default GroupSetup;
